import React from "react";
import { Redirect, Route } from "react-router-dom";
import {
  Card,
  TextField,
  FormControl,
  Input,
  InputLabel,
  Select,
  Chip,
  MenuItem,
  Slider,
  Switch,
  Tooltip,
} from "@material-ui/core";
import "./TeacherSettings.css";
import TooltipInfoIcon from "../../../../assets/icons/toolTipInfoIconBlue.svg";
import UserSettings from "../../../../interfaces/UserSettings";
import ReadleeProfile from "../../../../services/firebase/ReadleeProfile";
import FirebaseService from "../../../../services/firebase/FirebaseService";
import { NAME_MAX_LENGTH, EMAIL_MAX_LENGTH, GENERAL_MAX_LENGTH } from "../../../../utils/stringFormatting";

interface TeacherSettingsProps {
  userSettings: UserSettings | undefined;
  profile: ReadleeProfile | undefined;
}

const TeacherSettings = (props: TeacherSettingsProps) => {
  // Ensure the user is logged in
  if (!props.profile) {
    return <Route component={() => <Redirect to={{ pathname: "/Login" }} />} render={undefined} />;
  }

  const handleChangeProfileInfo = (property: string, event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value;
    if (typeof value !== "string") {
      return;
    }
    FirebaseService.updateReadleeProfile(props.profile?.uid || "", property, value).catch(() => {
      // TODO: Toast that it failed to save
    });
  };

  const handleChangeGrade = (event: React.ChangeEvent<{ value: unknown }>) => {
    const gradeList = event.target.value as string[];
    const grades: string[] = [];
    gradeList.forEach(group => {
      switch (group) {
        case "Elementary (K-5)":
          grades.push("K", "1", "2", "3", "4", "5");
          break;
        case "Middle School (6-8)":
          grades.push("6", "7", "8");
          break;
        case "High School (9-12)":
          grades.push("9", "10", "11", "12");
          break;
        case "Other":
          grades.push("Other");
          break;
      }
    });
    FirebaseService.updateReadleeProfile(props.profile?.uid || "", "grade", grades).catch(() => {
      // TODO: Toast that it failed to save
    });
  };

  const handleChangeFontSize = (_event: unknown, value: number | number[]) => {
    FirebaseService.updateUserSettings(props.profile?.uid || "", "readerFontSize", value).catch(() => {
      // TODO: Toast that it failed to save
    });
  };

  const handleChangeDataSharing = (event: React.ChangeEvent<HTMLInputElement>) => {
    FirebaseService.updateUserSettings(props.profile?.uid || "", event.target.name, event.target.checked).catch(() => {
      // TODO: Toast that it failed to save
    });
  };

  // for grades dropdown
  const gradeList = props.profile.grade.reduce((list: string[], grade: string) => {
    if (["K", "1", "2", "3", "4", "5"].includes(grade) && !list.includes("Elementary (K-5)"))
      list.push("Elementary (K-5)");
    else if (["6", "7", "8"].includes(grade) && !list.includes("Middle School (6-8)")) list.push("Middle School (6-8)");
    else if (["9", "10", "11", "12"].includes(grade) && !list.includes("High School (9-12)"))
      list.push("High School (9-12)");
    else if (grade === "Other") list.push("Other");
    return list;
  }, []);

  // for subjects dropdown
  const subjectsArray = [
    "General Education",
    "Language Arts",
    "Mathematics",
    "Science",
    "Social Studies",
    "Foreign Language",
    "Music",
  ];

  // for reader text size slider
  const marks = [
    {
      value: 12,
      label: "12",
    },
    {
      value: 16,
      label: "16",
    },
    {
      value: 20,
      label: "20",
    },
    {
      value: 24,
      label: "24",
    },
    {
      value: 28,
      label: "28",
    },
    {
      value: 32,
      label: "32",
    },
    {
      value: 36,
      label: "36",
    },
    {
      value: 40,
      label: "40",
    },
    {
      value: 44,
      label: "44",
    },
    {
      value: 48,
      label: "48",
    },
  ];
  return (
    <div className="TeacherSettings">
      <div className="Main">
        <div id="user-profile" className="UserProfile">
          <h1>User Profile</h1>
          <Card elevation={0}>
            <form className="ProfileInfo">
              <div className="row">
                <TextField
                  className="profileInfoInput"
                  value={props.profile.firstName}
                  onChange={event => {
                    handleChangeProfileInfo("firstName", event);
                  }}
                  label="First Name"
                  variant="outlined"
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                />
                <TextField
                  className="profileInfoInput"
                  value={props.profile.lastName}
                  onChange={event => {
                    handleChangeProfileInfo("lastName", event);
                  }}
                  label="Last Name"
                  variant="outlined"
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                />
                <TextField
                  disabled
                  className="profileInfoInput"
                  value={props.profile.email}
                  onChange={event => {
                    handleChangeProfileInfo("email", event);
                  }}
                  label="Email"
                  variant="outlined"
                  inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
                />
              </div>
              <div className="row">
                <TextField
                  className="profileInfoInput School"
                  value={props.profile.school}
                  onChange={event => {
                    handleChangeProfileInfo("school", event);
                  }}
                  label="School"
                  variant="outlined"
                  inputProps={{ maxLength: GENERAL_MAX_LENGTH }}
                />
                <FormControl className="Grades profileInfoInput chipInput">
                  <InputLabel variant="outlined">Grades</InputLabel>
                  {/* inputProps in Select needed to fix bug where width increases when menu opens (body { padding-right: 15}) */}
                  <Select
                    disableUnderline
                    multiple
                    value={gradeList}
                    onChange={handleChangeGrade}
                    inputProps={{ MenuProps: { disableScrollLock: true } }}
                    input={<Input />}
                    renderValue={selected => (
                      <div>
                        {(selected as string[]).map(value => (
                          <Chip key={value} label={value} />
                        ))}
                      </div>
                    )}
                  >
                    <MenuItem key="Elementary" value="Elementary (K-5)">
                      Elementary (K-5)
                    </MenuItem>
                    <MenuItem key="Middle" value="Middle School (6-8)">
                      Middle School (6-8)
                    </MenuItem>
                    <MenuItem key="High" value="High School (9-12)">
                      High School (9-12)
                    </MenuItem>
                    <MenuItem key="Other" value="Other">
                      Other
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="row">
                <FormControl className="Subjects profileInfoInput chipInput">
                  <InputLabel variant="outlined">Subjects</InputLabel>
                  {/* inputProps in Select needed to fix bug where width increases when menu opens (body { padding-right: 15}) */}
                  <Select
                    disableUnderline
                    multiple
                    value={props.profile?.subject}
                    onChange={event => {
                      handleChangeProfileInfo("subject", event);
                    }}
                    inputProps={{ MenuProps: { disableScrollLock: true } }}
                    input={<Input />}
                    renderValue={selected => (
                      <div>
                        {(selected as string[]).map(value => (
                          <Chip key={value} label={value} />
                        ))}
                      </div>
                    )}
                  >
                    {subjectsArray.map(name => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </form>
          </Card>
        </div>
        <div id="text-size" className="TextSize">
          <h1>Text Size</h1>
          <Card elevation={0}>
            <div className="ReaderTextSize">
              <div className="labelWithTooltip">
                <p>Reader Text Size</p>
                <Tooltip title={<span className="teacherTooltip">Determines size of text inside reader view</span>}>
                  <img src={TooltipInfoIcon} alt="Text Size" height={14} />
                </Tooltip>
              </div>
              <Slider
                track={false}
                value={props.userSettings?.readerFontSize}
                onChangeCommitted={handleChangeFontSize}
                marks={marks}
                min={12}
                max={48}
                step={null}
              />
            </div>
          </Card>
        </div>
        <div id="data-sharing" className="DataSharing">
          <h1>Data Sharing</h1>
          <Card elevation={0}>
            <p className="grayFont">Choose what data to share with students. You can adjust this on each assignment.</p>

            <div className="switch">
              <div className="labelWithTooltip">
                <p className="Completion">Assignment Completion Percentage</p>
                <Tooltip
                  title={
                    <span className="teacherTooltip">
                      This data point will let students know approximately how much of the passage they have completed.
                    </span>
                  }
                >
                  <img src={TooltipInfoIcon} alt="Assignment Completion Percentage" height={14} />
                </Tooltip>
              </div>
              <Switch
                checked={props.userSettings?.defaultCompletionOn}
                onChange={handleChangeDataSharing}
                name="defaultCompletionOn"
              />
            </div>
            <div className="switch">
              <div className="labelWithTooltip">
                <p>Words Per Minute</p>
                <Tooltip
                  title={
                    <span className="teacherTooltip">
                      This will inform students of their average reading pace (how many words do they read per minute?)
                    </span>
                  }
                >
                  <img src={TooltipInfoIcon} alt="Assignment Completion Percentage" height={14} />
                </Tooltip>
              </div>
              <Switch
                checked={props.userSettings?.defaultWPMOn}
                onChange={handleChangeDataSharing}
                name="defaultWPMOn"
              />
            </div>
            <div className="switch">
              <div className="labelWithTooltip">
                <p>Accuracy</p>
                <Tooltip
                  title={
                    <span className="teacherTooltip">
                      This will provide students with an approximation of how accurately they pronounce words in the
                      text.
                    </span>
                  }
                >
                  <img src={TooltipInfoIcon} alt="Assignment Completion Percentage" height={14} />
                </Tooltip>
              </div>
              <Switch
                checked={props.userSettings?.defaultAccuracyOn}
                onChange={handleChangeDataSharing}
                name="defaultAccuracyOn"
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeacherSettings;
